/**
 * Thin, abstracted HTTP client for the Proximi.io REST surface.
 *
 * Everything goes through {@link request} so that the Phase-2 Rust BFF
 * (`proximiio-mcpd`) can later back these calls *without touching callers*:
 * swap `API_BASE` (or route through the Vite `/api` proxy) and the auth/places/
 * floors/geo modules keep working. Responses are validated with the zod schema
 * passed by each module, so a malformed/partial payload fails loudly at the
 * boundary rather than leaking `any` into the UI.
 */
import type { z } from "zod";

/**
 * API base URL. Sourced from `VITE_API_BASE` at build/dev time; defaults to the
 * public Proximi.io gateway so the wired login call resolves out-of-the-box.
 * In production the bundle is served by the Rust BFF and this points at it
 * (or stays "/api" to use the Vite dev proxy).
 */
export const API_BASE: string =
  import.meta.env.VITE_API_BASE ?? "https://api.proximi.fi";

/**
 * Base URL for the Rust BFF (`proximiio-mcpd` / `crates/mcp-web`) — auth login
 * (`/auth/login`) and chat (`/chat`) go here, NOT to the public Proximi.io
 * gateway. Defaults to the relative `/api` so the Vite dev `server.proxy`
 * (vite.config.ts) and the production same-origin deployment both resolve it.
 * Map data calls (places/floors/geo) keep using {@link API_BASE} directly.
 */
export const BFF_BASE: string = import.meta.env.VITE_BFF_BASE ?? "/api";

export interface RequestOptions<T> {
  /** zod schema the JSON body is parsed against. */
  schema: z.ZodType<T>;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  /** JSON request body (serialised automatically). */
  body?: unknown;
  /** Bearer token for authenticated endpoints. */
  token?: string;
  /** Query params appended to the URL. */
  params?: Record<string, string | number | undefined>;
  /** Origin to target; defaults to {@link API_BASE}. Use {@link BFF_BASE} for the BFF. */
  base?: string;
  signal?: AbortSignal;
}

/** A failed HTTP request (non-2xx), carrying status + parsed body for the UI. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** Resolve `base + path` into an absolute URL, supporting relative bases
 * (e.g. `/api`) against the current origin so the Vite dev proxy works. */
export function resolveUrl(path: string, base: string = API_BASE): string {
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  const full = `${trimmed}${path.startsWith("/") ? path : `/${path}`}`;
  if (/^https?:\/\//.test(full)) return full;
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  return new URL(full, origin).toString();
}

function buildUrl(
  path: string,
  params?: RequestOptions<unknown>["params"],
  base: string = API_BASE,
): string {
  const url = new URL(resolveUrl(path, base));
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Perform a JSON request and validate the response against `schema`.
 * Throws {@link ApiError} on non-2xx, and re-throws zod errors on schema drift.
 */
export async function request<T>(
  path: string,
  { schema, method = "GET", body, token, params, base, signal }: RequestOptions<T>,
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(buildUrl(path, params, base), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  const text = await response.text();
  const json: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Request to ${path} failed (${response.status})`,
      json,
    );
  }

  return schema.parse(json);
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
